import { useNavigate } from 'react-router-dom';
import { Home } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import type { Unidad } from '@condo/contracts';
import { useCondoContext } from '@/features/auth/use-condo-context';
import { useUnidades } from '@/features/unidades/api';
import { DataTable } from '@/components/common/data-table';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/common/empty-state';
import { formatM2 } from '@/lib/format/number';
import { formatAlicuotaFraction } from '@/lib/format/number';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';

const columns: ColumnDef<Unidad>[] = [
  {
    accessorKey: 'numero',
    header: 'Unidad',
    cell: ({ row }) => (
      <span className="font-display text-sm font-semibold">{row.original.numero}</span>
    ),
  },
  {
    accessorKey: 'tipo',
    header: 'Tipo',
    cell: ({ row }) => (
      <Badge variant="neutral">{row.original.tipo}</Badge>
    ),
  },
  {
    accessorKey: 'piso',
    header: 'Piso',
    cell: ({ row }) => (
      <span className="tabular">
        {row.original.piso === null || row.original.piso === undefined
          ? '—'
          : row.original.piso}
      </span>
    ),
  },
  {
    accessorKey: 'm2',
    header: 'Superficie',
    cell: ({ row }) => <span className="tabular">{formatM2(row.original.m2)}</span>,
  },
  {
    accessorKey: 'alicuota',
    header: 'Alicuota',
    cell: ({ row }) => (
      <span className="font-display tabular font-semibold text-[var(--color-primary)]">
        {formatAlicuotaFraction(row.original.alicuota)}
      </span>
    ),
  },
  {
    accessorKey: 'dormitorios',
    header: 'Dorm.',
    cell: ({ row }) => (
      <span className="tabular">{row.original.dormitorios ?? '—'}</span>
    ),
  },
];

export function UnidadesRoute() {
  const { condoId } = useCondoContext();
  const { data, isLoading } = useUnidades(condoId);
  const navigate = useNavigate();
  const unidades = data?.items ?? [];

  const sumAlicuotas = unidades.reduce((s, u) => s + u.alicuota, 0);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Inventario fisico"
        title="Unidades"
        subtitle="Listado completo de viviendas, estacionamientos y bodegas del condominio. La suma de alicuotas debe totalizar 100%."
        meta={
          <div className="mt-4 flex items-center gap-4 text-sm">
            <div className="flex items-baseline gap-2">
              <span className="eyebrow">Total unidades</span>
              <span className="font-display tabular font-semibold">{unidades.length}</span>
            </div>
            <span className="h-4 w-px bg-[var(--color-hairline-strong)]" />
            <div className="flex items-baseline gap-2">
              <span className="eyebrow">Sumatoria alicuota</span>
              <span
                className={`font-display tabular font-semibold ${
                  Math.abs(sumAlicuotas - 1) < 0.000001
                    ? 'text-[var(--color-moss)]'
                    : 'text-[var(--color-ochre)]'
                }`}
              >
                {formatAlicuotaFraction(sumAlicuotas)}
              </span>
            </div>
          </div>
        }
      />

      {unidades.length === 0 && !isLoading ? (
        <EmptyState
          icon={<Home className="h-5 w-5" />}
          title="Sin unidades registradas"
          description="Las unidades se crean al configurar el condominio."
        />
      ) : (
        <DataTable
          columns={columns}
          data={unidades}
          isLoading={isLoading}
          onRowClick={(u) => navigate(`/app/condominios/${condoId}/unidades/${u.id}`)}
        />
      )}
    </PageContainer>
  );
}
